import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

/**
 * HomePage — learner-app landing.
 *
 * Layout mirrors the marketing hero pattern (see apps/marketing/src/components/Hero.astro):
 * centered max-w-6xl, slate display headline, amber underline-accent on the
 * sub-brow, body copy in neutral-700, then a status card for the Phase-0
 * backend-health ping. Real prep-flow lands in ROADS-147+.
 */

/**
 * Backend health endpoint — confirms the Ktor dev server is reachable. The Vite dev
 * server proxies /api → http://localhost:31800, but in this Phase-0 ping we hit the
 * absolute URL so the check still works when the SPA is served from elsewhere
 * (e.g. `vite preview` on a different port).
 */
const HEALTH_URL = "http://localhost:31800/health";

type HealthResponse = {
  status: string;
  [key: string]: unknown;
};

async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch(HEALTH_URL);
  if (!res.ok) throw new Error(`Health check failed: ${res.status}`);
  // Backend returns JSON; if it regressed to plain text we still tolerate it.
  const text = await res.text();
  try {
    return JSON.parse(text) as HealthResponse;
  } catch {
    return { status: text.trim() };
  }
}

export default function HomePage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["health"],
    queryFn: fetchHealth,
  });

  return (
    <section className="mx-auto max-w-6xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
      <div className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
          {t("home.eyebrow")}
        </p>
        <h1 className="mt-sm font-display text-4xl font-bold tracking-tight text-brand-primary sm:text-5xl md:text-6xl">
          {t("home.title")}
          <span className="mt-xs block text-neutral-800 sm:mt-sm">{t("home.subhead")}</span>
        </h1>
        <p className="mt-md text-lg text-neutral-800 sm:text-xl">{t("home.intro")}</p>
      </div>

      {/*
        Phase-0 status card — one responsibility: surface whether the learner
        app can reach the Ktor dev server. Real product surfaces (rule-diff
        intake, practice test, handbook browser) land in ROADS-147+.
      */}
      <div
        aria-live="polite"
        data-testid="health-status"
        className="mt-xl max-w-xl rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm"
      >
        <div className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
          {t("home.status")}
        </div>
        {isLoading && <div className="mt-xs font-medium text-neutral-800">{t("home.loading")}</div>}
        {isError && (
          <div className="mt-xs font-medium text-danger">
            {t("home.error")}: {error instanceof Error ? error.message : "unknown"}
          </div>
        )}
        {data && <div className="mt-xs font-medium text-success">{data.status}</div>}
      </div>
    </section>
  );
}
